import { useContext, useEffect } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom-v5-compat";
import { useModuleBEM, IconNameEnum } from "@b2bportal/core-themes";
import { useDeviceType, TRIPS_PATH } from "@b2bportal/core-utilities";
import type { RbcPartnerMetadata } from "@b2bportal/rbc-accounts-api";
import { useI18nContext } from "@hopper-b2b/i18n";
import { Skeleton } from "@material-ui/lab";
import { Icon } from "@tenant/ui";
import styles from "./styles.module.scss";
import { UserContext } from "@/App";
import { AccountSelectors, useGetPointsBalance } from "@/modules/account";
import { Experiments, useExperiment } from "@/modules/experiments/src";
import { TenantIconNameEnum } from "@/types/tenantIcons";
import { WALLET_PATH } from "@/utilities";

export const PointsBanner = () => {
  const { formatRewardsCurrency, t } = useI18nContext();
  const { isDesktopAndUp } = useDeviceType();
  const { pointsBalance, getPointsBalance } = useGetPointsBalance();
  const { isExperimentVariant } = useExperiment();
  const walletEnabled = isExperimentVariant(
    Experiments.WALLET_ENABLED.name,
    Experiments.WALLET_ENABLED.variants.available
  );

  const { sessionInfo } = useContext(UserContext);
  const { language } = useI18nContext();
  const { accountNameEn, accountNameFr } = (sessionInfo?.userInfo
    ?.partnerMetadata ?? {}) as RbcPartnerMetadata;
  const cardType = language === "en-CA" ? accountNameEn : accountNameFr;
  const isRewardsLoading = useSelector(AccountSelectors.isRewardsLoading);
  const hasError = useSelector(AccountSelectors.getRewardsError);

  useEffect(() => {
    if (pointsBalance === null || pointsBalance === undefined) {
      getPointsBalance(true);
    }
  }, [pointsBalance, getPointsBalance]);

  const [block, cn] = useModuleBEM(styles, "PointsBanner");
  return (
    <div className={block}>
      <div className={cn("points-content")}>
        <div className={cn("left-content")}>
          {!hasError && (
            <>
              {isDesktopAndUp && (
                <>
                  {isRewardsLoading ? (
                    <Skeleton variant="text" width={100} height={80} />
                  ) : (
                    <p
                      className={cn("card-info-container")}
                    >{`${cardType || ""}`}</p>
                  )}
                </>
              )}
              {isRewardsLoading ? (
                <Skeleton variant="text" width={100} height={80} />
              ) : (
                pointsBalance && (
                  <p className={cn("points-balance-amount")}>
                    {formatRewardsCurrency(pointsBalance.totalPoints, {
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 0,
                    })}
                  </p>
                )
              )}
            </>
          )}
        </div>
        <div className={cn("right-actions")}>
          <Link
            to={TRIPS_PATH}
            className={cn("my-trips-link")}
            aria-label={
              t("trips.navigateToMyTrips") || "Navigate to My Trips page"
            }
          >
            {t("myTrips")}
            <Icon iconName={IconNameEnum.rightNavChevron} useCurrentColor />
          </Link>
          {walletEnabled && (
            <Link
              to={WALLET_PATH}
              className={cn("wallet-link")}
              aria-label={t("walletPage.title")}
            >
              <Icon iconName={TenantIconNameEnum.wallet} />
            </Link>
          )}
        </div>
      </div>
    </div>
  );
};
